import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Star, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { StatCard, Card, Spinner, EmptyState } from "../components/dashboard/DashboardShell";
import OrdersTable from "../components/dashboard/OrdersTable";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { rupiah, fmtDate } from "../lib/format";

export default function InspectorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: d, isLoading } = useQuery({ queryKey: ["inspector-dashboard"], queryFn: () => api.get("/inspectors/me/dashboard").then((r) => r.data) });
  if (isLoading || !d) return <Spinner />;
  const monthly = d.earnings.monthly.map((m) => ({ ...m, bulan: fmtDate(m.bulan + "-01", "MMM") }));
  const up = d.earnings.growth_percent >= 0;
  const stats = [
    { label: "Pesanan Baru", value: d.stats.pesanan_baru, icon: "inbox", color: "text-blue-600 bg-blue-50", testId: "stat-pesanan-baru" },
    { label: "Dijadwalkan", value: d.stats.dijadwalkan, icon: "calendar", color: "text-amber-600 bg-amber-50", testId: "stat-dijadwalkan" },
    { label: "Sedang Berlangsung", value: d.stats.berlangsung, icon: "loader", color: "text-violet-600 bg-violet-50", testId: "stat-berlangsung" },
    { label: "Selesai", value: d.stats.selesai, icon: "check", color: "text-emerald-600 bg-emerald-50", testId: "stat-selesai" },
  ];

  return (
    <div data-testid="inspector-dashboard">
      <h1 className="text-2xl font-extrabold text-slate-900">Dashboard Inspector</h1>
      <p className="text-sm text-slate-500 mt-1">Halo, <span className="font-bold text-slate-700">{user.name}</span>. Terus berikan layanan terbaik, bantu lebih banyak orang mendapatkan kendaraan yang aman.</p>

      {d.verification_status !== "verified" && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="verification-warning">
          <p className="text-sm font-bold text-amber-700 flex items-center gap-2"><AlertTriangle size={16} /> Akun Anda {d.verification_status === "rejected" ? "ditolak" : "belum diverifikasi"}. Profil belum tampil di pencarian pengguna.</p>
          <Button size="sm" onClick={() => navigate("/inspector/profil")} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg">Lengkapi Profil</Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">{stats.map((s) => <StatCard key={s.label} {...s} />)}</div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
        <Card>
          <p className="text-xs font-semibold text-slate-400">Pendapatan Bulan Ini</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="dash-earnings">{rupiah(d.earnings.this_month)}</p>
          <p className={`text-[11px] font-bold flex items-center gap-1 mt-1 ${up ? "text-emerald-500" : "text-red-500"}`}>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {Math.abs(d.earnings.growth_percent)}% dari bulan lalu</p>
          <div className="h-36 mt-4">
            {monthly.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} barSize={22}>
                  <XAxis dataKey="bulan" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis hide />
                  <Bar dataKey="total" radius={[6, 6, 6, 6]}>{monthly.map((e, i) => <Cell key={i} fill={i === monthly.length - 1 ? "#2563eb" : "#93c5fd"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center pt-10">Belum ada pendapatan.</p>}
          </div>
        </Card>
        <Card>
          <p className="text-xs font-semibold text-slate-400">Saldo Tersedia</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="dash-balance">{rupiah(d.balance)}</p>
          <Button onClick={() => navigate("/inspector/pencairan")} data-testid="withdraw-btn" className="w-full mt-5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11 shadow-lg shadow-blue-600/25">Tarik Dana</Button>
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">Pencairan diproses admin dalam 1×24 jam kerja.</p>
        </Card>
        <Card className="md:col-span-2 xl:col-span-1">
          <p className="text-xs font-semibold text-slate-400">Rating Saya</p>
          <div className="flex items-center gap-2 mt-1"><Star size={22} className="text-amber-400" fill="#fbbf24" /><p className="text-2xl font-extrabold text-slate-900" data-testid="dash-rating">{d.rating.avg} / 5</p></div>
          <p className="text-[11px] text-slate-400 font-semibold mt-1">{d.rating.count} ulasan</p>
          <div className="mt-3 space-y-1.5">
            {d.rating.breakdown.map((r) => (
              <div key={r.star} className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 w-2">{r.star}</span><Star size={10} className="text-amber-400" fill="#fbbf24" />
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${d.rating.count ? (r.count / d.rating.count) * 100 : 0}%` }} /></div>
                <span className="text-[11px] text-slate-400 w-6 text-right">{r.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        <Card title="Pesanan Terbaru" link="Lihat Semua" onLink={() => navigate("/inspector/pesanan")} className="xl:col-span-2">
          <OrdersTable orders={d.recent_orders.map((o) => ({ ...o, user: o.user || { name: "-" } }))} compact />
        </Card>
        <Card title="Jadwal Hari Ini" link="Lihat Semua" onLink={() => navigate("/inspector/jadwal")}>
          {!d.today_schedule.length ? <EmptyState icon="calendar" title="Tidak ada jadwal hari ini" /> : (
            <div className="space-y-0" data-testid="today-schedule">
              {d.today_schedule.map((j, i) => (
                <div key={j.order_id} className="flex gap-4">
                  <div className="flex flex-col items-center"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 mt-1.5" />{i < d.today_schedule.length - 1 && <span className="w-px flex-1 bg-slate-200" />}</div>
                  <button onClick={() => navigate(`/inspector/pesanan/${j.order_id}`)} className="pb-6 text-left group">
                    <p className="text-sm font-extrabold text-slate-900">{j.scheduled_time}</p>
                    <p className="text-[13px] font-semibold text-slate-600 mt-0.5 group-hover:text-blue-600">{j.vehicle_type_label}</p>
                    <p className="text-xs text-slate-400">{j.address}, {j.city}</p>
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
